import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Link, createFileRoute, useNavigate } from '@tanstack/react-router';
import { Loader2, Sparkles } from 'lucide-react';
import { ConfidenceBadge } from '@/components/ai-accountant/confidence-chip';
import { AiStatusChip } from '@/components/ai-accountant/status-chip';
import { EmptyState, PageHeader } from '@/components/page-header';
import { FileDrop } from '@/components/portal/file-drop';
import { toast } from '@/components/toast';
import { Button } from '@/components/ui/button';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  recordAsLabel,
  useAiDocuments,
  useAiSettings,
  useAiSummary,
  type AiDocument,
  type AiFilter,
} from '@/lib/ai-accountant';
import { api } from '@/lib/api';
import { formatAmount, formatDate, formatDateTime } from '@/lib/format';
import { useT } from '@/lib/i18n';
import { useOrgId } from '@/lib/org';
import { documentPath, errorText, isClientRole, uploadFile, useOrgRole } from '@/lib/portal';
import { cn } from '@/lib/utils';

const CHIPS = [
  ['processing', 'processing'],
  ['for_review', 'forReview'],
  ['recorded', 'recorded'],
  ['rejected', 'rejected'],
  ['failed', 'failed'],
  ['all', 'all'],
] as const satisfies ReadonlyArray<readonly [AiFilter, string]>;

/** PDFs and JPG or PNG photos (the server checks again by content). */
const AI_ACCEPT = '.pdf,.png,.jpg,.jpeg,application/pdf,image/png,image/jpeg';
const aiFileOk = (f: File) =>
  /^(application\/pdf|image\/png|image\/jpeg)$/.test(f.type) || /\.(pdf|png|jpe?g)$/i.test(f.name);

export const Route = createFileRoute('/ai-accountant/')({
  component: AiAccountant,
  validateSearch: (s: Record<string, unknown>): { status?: AiFilter } =>
    typeof s['status'] === 'string' && CHIPS.some(([k]) => k === s['status'])
      ? { status: s['status'] as AiFilter }
      : {},
});

/**
 * Screen AI1 (document 06): AI Accountant. Upload bills, receipts and sales invoices; each is read (OCR),
 * categorised against the chart of accounts and waits For review. Nothing is recorded until a
 * person approves it.
 */
function AiAccountant() {
  const t = useT();
  const orgId = useOrgId();
  const role = useOrgRole();
  const { status = 'for_review' } = Route.useSearch();
  const navigate = useNavigate();
  const list = useAiDocuments(status);
  const summary = useAiSummary();
  const settings = useAiSettings();
  const qc = useQueryClient();

  const upload = useMutation({
    mutationFn: async (files: File[]) => {
      for (const f of files) await uploadFile(`/orgs/${orgId}/ai-accountant`, f);
    },
    onSuccess: (_r, files) => {
      toast(
        files.length === 1
          ? t('reportPages.ai.list.uploadedOne', { name: files[0]!.name })
          : t('reportPages.ai.list.uploadedMany', { count: files.length }),
      );
      if (status !== 'processing' && status !== 'all')
        void navigate({ to: '/ai-accountant', search: { status: 'processing' } });
    },
    onError: (e) => toast(errorText(e), { tone: 'error' }),
    onSettled: () => qc.invalidateQueries({ queryKey: ['ai-accountant', orgId] }),
  });
  const rerun = useMutation({
    mutationFn: (d: AiDocument) =>
      api(`/orgs/${orgId}/ai-accountant/${d.id}/rerun`, {
        method: 'POST',
        body: JSON.stringify({ version: d.version }),
      }),
    onSuccess: () => toast(t('reportPages.ai.list.sentAgain')),
    onError: (e) => toast(errorText(e), { tone: 'error' }),
    onSettled: () => qc.invalidateQueries({ queryKey: ['ai-accountant', orgId] }),
  });

  if (!orgId)
    return <EmptyState title={t('shell.selectClient')} hint={t('home.selectClientHint')} />;
  if (role && isClientRole(role)) return <EmptyState title={t('reportPages.ai.list.staffOnly')} />;
  const rows = list.data ?? [];
  const counts = summary.data;
  const s = settings.data;
  return (
    <>
      <PageHeader
        title={t('reportPages.ai.list.title')}
        description={t('reportPages.ai.list.description')}
      />
      <div className="mb-5 grid gap-3 rounded-lg border bg-card p-4 md:grid-cols-[1fr_280px]">
        <FileDrop
          disabled={upload.isPending}
          accept={AI_ACCEPT}
          types={t('reportPages.ai.list.fileTypes')}
          label={
            upload.isPending
              ? t('reportPages.ai.list.uploading')
              : t('reportPages.ai.list.dropLabel')
          }
          onFiles={(files) => {
            const ok = files.filter(aiFileOk);
            for (const f of files.filter((x) => !aiFileOk(x)))
              toast(t('reportPages.ai.list.notAccepted', { name: f.name }), { tone: 'error' });
            if (ok.length) upload.mutate(ok);
          }}
          onProblem={(m) => toast(m, { tone: 'error' })}
        />
        <div className="space-y-2 text-sm" data-testid="ai-engine">
          <p className="flex items-center gap-1.5 font-medium">
            <Sparkles className="size-4 text-primary" aria-hidden />
            {s?.typesafeConfigured && s.sendToTypesafe
              ? t('reportPages.ai.list.engineJev', { model: s.model })
              : t('reportPages.ai.list.engineRules')}
          </p>
          <p className="text-muted-foreground">
            {s?.typesafeConfigured && s.sendToTypesafe
              ? t('reportPages.ai.list.privacyJev')
              : s?.typesafeConfigured
                ? t('reportPages.ai.list.typesafeAvailable')
                : t('reportPages.ai.list.nothingLeaves')}
          </p>
          <p className="text-muted-foreground">
            {t('reportPages.ai.list.maxPages', { count: s?.maxPages ?? 5 })}
          </p>
        </div>
      </div>

      <div
        className="mb-3 flex flex-wrap gap-2"
        role="tablist"
        aria-label={t('reportPages.ai.list.statusAria')}
      >
        {CHIPS.map(([k, key]) => {
          const n = k === 'all' ? null : counts?.[k];
          return (
            <button
              key={k}
              role="tab"
              aria-selected={status === k}
              className={cn(
                'rounded-full border px-3 py-1 text-sm',
                status === k ? 'border-primary bg-primary/10 text-primary' : 'bg-card',
              )}
              onClick={() => void navigate({ to: '/ai-accountant', search: { status: k } })}
            >
              {t(`reportPages.ai.status.${key}`)}
              {n ? <span className="tnum ms-1.5 text-xs text-muted-foreground">{n}</span> : null}
            </button>
          );
        })}
      </div>

      {list.isPending ? (
        <p className="text-sm text-muted-foreground">{t('common.loading')}</p>
      ) : list.isError ? (
        <EmptyState title={t('reportPages.ai.list.loadError')} hint={list.error.message} />
      ) : !rows.length ? (
        <EmptyState
          title={
            status === 'for_review'
              ? t('reportPages.ai.list.nothingToReview')
              : t('reportPages.ai.list.noDocuments')
          }
          hint={t('reportPages.ai.list.emptyHint')}
        />
      ) : (
        <div className="rounded-lg border bg-card">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-[140px]">{t('reportPages.ai.list.colUploaded')}</TableHead>
                <TableHead>{t('reportPages.ai.list.colFile')}</TableHead>
                <TableHead>{t('reportPages.ai.list.colParty')}</TableHead>
                <TableHead className="w-[100px]">{t('reportPages.ai.list.colDate')}</TableHead>
                <TableHead className="w-[140px] text-end">
                  {t('reportPages.ai.list.colTotal')}
                </TableHead>
                <TableHead className="w-[110px]">{t('reportPages.ai.list.colRecordAs')}</TableHead>
                <TableHead className="w-[120px]">
                  {t('reportPages.ai.list.colConfidence')}
                </TableHead>
                <TableHead className="w-[110px]">{t('reportPages.ai.list.colStatus')}</TableHead>
                <TableHead className="w-[150px] text-end">
                  {t('reportPages.ai.list.colAction')}
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((d) => (
                <TableRow
                  key={d.id}
                  data-testid="ai-row"
                  className="cursor-pointer"
                  onClick={() => void navigate({ to: '/ai-accountant/$id', params: { id: d.id } })}
                >
                  <TableCell className="tnum">{formatDateTime(d.uploadedAt)}</TableCell>
                  <TableCell className="max-w-[240px] truncate font-medium">{d.fileName}</TableCell>
                  <TableCell className="max-w-[220px] truncate">{d.vendorName}</TableCell>
                  <TableCell className="tnum">
                    {d.documentDate ? formatDate(d.documentDate) : ''}
                  </TableCell>
                  <TableCell className="tnum text-end">
                    {d.total ? `${d.currency ?? ''} ${formatAmount(d.total)}` : ''}
                  </TableCell>
                  <TableCell>{d.recordAs ? recordAsLabel(d.recordAs) : ''}</TableCell>
                  <TableCell>
                    {d.status === 'for_review' ? <ConfidenceBadge value={d.confidence} /> : null}
                  </TableCell>
                  <TableCell>
                    <AiStatusChip status={d.status} />
                  </TableCell>
                  <TableCell className="text-end" onClick={(e) => e.stopPropagation()}>
                    <RowAction d={d} onRerun={() => rerun.mutate(d)} />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
    </>
  );
}

function RowAction({ d, onRerun }: { d: AiDocument; onRerun: () => void }) {
  const t = useT();
  if (d.status === 'processing')
    return (
      <span className="inline-flex items-center gap-1 text-sm text-muted-foreground">
        <Loader2 className="size-3.5 animate-spin" aria-hidden /> {t('reportPages.ai.list.reading')}
      </span>
    );
  if (d.status === 'for_review')
    return (
      <Button size="sm" asChild>
        <Link to="/ai-accountant/$id" params={{ id: d.id }}>
          {t('reportPages.ai.list.review')}
        </Link>
      </Button>
    );
  if (d.status === 'failed')
    return (
      <Button size="sm" variant="outline" onClick={onRerun}>
        {t('reportPages.ai.list.rerun')}
      </Button>
    );
  if (d.status === 'recorded' && d.document)
    return (
      <Link
        className="text-sm text-primary hover:underline"
        to={documentPath(d.document.kind, d.document.id)}
      >
        {t(
          d.document.kind === 'expense'
            ? 'reportPages.ai.list.docExpense'
            : d.document.kind === 'invoice'
              ? 'reportPages.ai.list.docInvoice'
              : 'reportPages.ai.list.docBill',
          { number: d.document.number ?? '' },
        )}
      </Link>
    );
  if (d.status === 'recorded' && d.bankImport)
    return (
      <Link
        className="text-sm text-primary hover:underline"
        to="/banking"
        search={{ accountId: d.bankImport.accountId }}
      >
        {t('reportPages.ai.list.banking', { lines: d.bankImport.lines })}
      </Link>
    );
  if (d.status === 'recorded' && d.journalEntry)
    return (
      <Link
        className="text-sm text-primary hover:underline"
        to="/accounting/journal-entries/$entryId"
        params={{ entryId: d.journalEntry.id }}
      >
        {t('reportPages.ai.list.journal', { number: d.journalEntry.entryNo })}
      </Link>
    );
  return (
    <Link
      className="text-sm text-primary hover:underline"
      to="/ai-accountant/$id"
      params={{ id: d.id }}
    >
      {t('reportPages.ai.list.view')}
    </Link>
  );
}
